<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { ArrowLeft } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'
  import FileField from '@/components/admin/form/FileField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import {
    PdfFormTemplatesService,
    OrganisationsService,
    type SuperUserOrganisation as Organisation
  } from '@/types/generated'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      backToTemplates: 'Back to document templates',
      templateInfo: 'Template Information',
      configureSettings: 'Configure your PDF form template settings',
      templateName: 'Template Name',
      enterTemplateName: 'Enter template name',
      pdfDocument: 'PDF Document',
      organization: 'Organization',
      selectOrganization: 'Select an organization',
      creating: 'Creating...',
      createTemplate: 'Create Template'
    },
    de: {
      backToTemplates: 'Zurück zu Dokumentenvorlagen',
      templateInfo: 'Vorlageninformationen',
      configureSettings: 'Konfigurieren Sie Ihre PDF-Formularvorlageneinstellungen',
      templateName: 'Vorlagenname',
      enterTemplateName: 'Vorlagennamen eingeben',
      pdfDocument: 'PDF-Dokument',
      organization: 'Organisation',
      selectOrganization: 'Organisation auswählen',
      creating: 'Wird erstellt...',
      createTemplate: 'Vorlage erstellen'
    }
  })

  function getCsrfToken(): string | undefined {
    const value = `; ${document.cookie}`
    const parts = value.split(`; csrftoken=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift()
  }

  const router = useRouter()
  const route = useRoute()

  const isAdminRoute = computed(() => route.path.startsWith('/admin/dashboard'))

  const backRoute = computed(() => {
    if (isAdminRoute.value) {
      return ROUTES.ADMIN.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_LIST
    }
    return ROUTES.APP.SETTINGS.DOCUMENT_TEMPLATES
  })

  const form = ref({
    name: 'New document template',
    original_pdf: null as File | null,
    org_uuid: null as string | null
  })

  const organisations = ref<Organisation[]>([])
  const organizationOptions = computed<Option[]>(
    () =>
      organisations.value?.map((org) => ({
        value: org.uuid,
        title: org.name || ''
      })) || []
  )

  const errors = ref<Record<string, string>>({})
  const loading = ref(false)

  const handleFileChange = (file: File | null) => {
    form.value.original_pdf = file
    if (form.value.name === 'New document template' || !form.value.name) {
      form.value.name = file?.name.split('.')[0] || 'New document template'
    }
    if (file) {
      errors.value.original_pdf = ''
    }
  }

  const searchOrganizations = async (query: string, oldQuery: string) => {
    if (query !== oldQuery) {
      try {
        const result = await OrganisationsService.organisationsList({
          limit: query ? 0 : 20,
          search: query || undefined
        })
        organisations.value = result.results as Organisation[]
      } catch (error) {
        console.error('Error searching organizations:', error)
      }
    }
  }

  const goBack = () => {
    router.push(backRoute.value)
  }

  onMounted(async () => {
    if (isAdminRoute.value) {
      organisations.value = (await OrganisationsService.organisationsList({ limit: 20 }))
        .results as Organisation[]
      if (organisations.value.length > 0) {
        form.value.org_uuid = organisations.value[0].uuid
      }
    }
  })

  const createTemplate = async () => {
    if (!form.value.original_pdf || !form.value.name.trim()) {
      if (!form.value.name.trim()) {
        errors.value.name = 'Name is required'
      }
      if (!form.value.original_pdf) {
        errors.value.original_pdf = 'PDF file is required'
      }
      return
    }

    errors.value = {}

    try {
      loading.value = true

      const formData = new FormData()
      formData.append('name', form.value.name.trim())
      formData.append('original_pdf', form.value.original_pdf)

      if (isAdminRoute.value && form.value.org_uuid) {
        formData.append('org_uuid', form.value.org_uuid)
      }

      const csrfToken = getCsrfToken()
      const headers: HeadersInit = {}
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken
      }

      const response = await fetch('/api/v3/pdf_form_templates/', {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(JSON.stringify(errorData))
      }

      const responseData = await response.json()

      const templateId = responseData.uuid || responseData.id
      if (templateId) {
        if (isAdminRoute.value) {
          router.push(
            ROUTES.ADMIN.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_DETAIL.replace(':uuid', templateId)
          )
        } else {
          router.push(
            `${ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES}/${templateId}/edit`
          )
        }
      } else {
        console.error('No UUID/ID in response:', responseData)
        router.push(backRoute.value)
      }
    } catch (error: any) {
      console.error('Failed to create PDF form template:', error)
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
      <div class="flex items-center gap-4">
        <button
          class="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-gray-900"
          @click="goBack"
        >
          <ArrowLeft class="h-4 w-4" />
          {{ t('backToTemplates') }}
        </button>
      </div>
    </div>

    <!-- Main content -->
    <div class="p-6">
      <div class="mx-auto max-w-4xl">
        <div class="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
          <div class="space-y-8">
            <!-- Form Header -->
            <div>
              <h2 class="mb-2 text-lg font-medium text-gray-900">{{ t('templateInfo') }}</h2>
              <p class="text-sm text-gray-600">{{ t('configureSettings') }}</p>
            </div>

            <!-- Organization (Admin only) -->
            <div v-if="isAdminRoute">
              <SelectField
                v-model="form.org_uuid"
                :label="t('organization')"
                name="organization"
                :options="organizationOptions"
                :is-filterable="true"
                :placeholder="t('selectOrganization')"
                :input-action="searchOrganizations"
              />
            </div>

            <!-- Template Name -->
            <div>
              <InputField
                v-model="form.name"
                name="name"
                :label="t('templateName')"
                :placeholder="t('enterTemplateName')"
              />
              <div
                v-if="errors.name"
                class="mt-1 text-sm text-red-600"
              >
                {{ errors.name }}
              </div>
            </div>

            <!-- PDF Upload -->
            <div>
              <FileField
                name="original_pdf"
                :label="t('pdfDocument')"
                :allowedFileExtensions="['pdf']"
                @update:model-value="handleFileChange"
              />
              <div
                v-if="errors.original_pdf"
                class="mt-2 text-sm text-red-600"
              >
                {{ errors.original_pdf }}
              </div>
            </div>

            <!-- Create Button -->
            <div class="flex justify-end">
              <Button
                :disabled="loading || !form.original_pdf || !form.name.trim()"
                class="flex items-center gap-2"
                @click="createTemplate"
              >
                {{ loading ? t('creating') : t('createTemplate') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
